import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { formatGBP } from '@/lib/utils';
import { format, parseISO, isFuture, startOfDay } from 'date-fns';
import CustomerLayout from '@/components/CustomerLayout';
const SLOT_LABELS = {
    AM: 'AM',
    PM: 'PM',
    full_day: 'Full Day',
};
const STATUS_STYLES = {
    confirmed: 'badge-green',
    pending_payment: 'badge-amber',
    cancelled: 'badge-red',
    completed: 'badge-grey',
};
const STATUS_LABELS = {
    confirmed: 'Confirmed',
    pending_payment: 'Pending',
    cancelled: 'Cancelled',
    completed: 'Completed',
};
export default function CustomerBookings() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [assessmentAppt, setAssessmentAppt] = useState(null);
    const [dogMap, setDogMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('upcoming');
    const [cancelModal, setCancelModal] = useState(null);
    const [contactModal, setContactModal] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        if (!user)
            return;
        Promise.all([
            supabase
                .from('bookings')
                .select('*, booking_extras(extra_services(name))')
                .eq('customer_id', user.id)
                .order('booking_date', { ascending: false }),
            supabase.from('settings').select('key,value').in('key', ['cancellation_cutoff_hours', 'edit_cutoff_time', 'customer_cancellation_enabled']),
            supabase.from('dogs').select('id,name').eq('owner_id', user.id),
            supabase.from('assessments')
                .select('id,preferred_assessment_date,dog_name')
                .eq('profile_id', user.id)
                .not('preferred_assessment_date', 'is', null)
                .in('status', ['pending', 'accepted'])
                .order('preferred_assessment_date', { ascending: true })
                .limit(1)
                .maybeSingle(),
        ]).then(([bookingsRes, settingsRes, dogsRes, assessRes]) => {
            setBookings(bookingsRes.data ?? []);
            setAssessmentAppt(assessRes.data ?? null);
            if (settingsRes.data) {
                const raw = Object.fromEntries(settingsRes.data.map((r) => [r.key, r.value]));
                const parseStr = (v) => {
                    if (typeof v === 'string') {
                        try {
                            return JSON.parse(v);
                        }
                        catch {
                            return v;
                        }
                    }
                    return v;
                };
                setSettings({
                    cancellation_cutoff_hours: Number(parseStr(raw.cancellation_cutoff_hours) ?? 48),
                    edit_cutoff_time: String(parseStr(raw.edit_cutoff_time) ?? '18:00'),
                    customer_cancellation_enabled: parseStr(raw.customer_cancellation_enabled) !== false,
                });
            }
            const map = {};
            (dogsRes.data ?? []).forEach((d) => { map[d.id] = d.name; });
            setDogMap(map);
            setLoading(false);
        });
    }, [user]);
    function isUpcoming(b) {
        return isFuture(startOfDay(parseISO(b.booking_date)));
    }
    function withinCancellationWindow(b) {
        const hours = settings?.cancellation_cutoff_hours ?? 48;
        const cutoff = new Date(parseISO(b.booking_date).getTime() - hours * 60 * 60 * 1000);
        return new Date() < cutoff;
    }
    function canEdit(b) {
        if (b.status !== 'confirmed' || !isUpcoming(b))
            return false;
        const bookingDate = parseISO(b.booking_date);
        const now = new Date();
        const daysDiff = Math.floor((startOfDay(bookingDate).getTime() - startOfDay(now).getTime()) / (1000 * 60 * 60 * 24));
        if (daysDiff > 1)
            return true;
        if (daysDiff === 1) {
            const [h, m] = (settings?.edit_cutoff_time ?? '18:00').split(':').map(Number);
            const cutoff = new Date(now);
            cutoff.setHours(h, m, 0, 0);
            return now < cutoff;
        }
        return false;
    }
    function showCancelButton(b) {
        return b.status === 'confirmed' && isUpcoming(b);
    }
    function canSelfCancel(b) {
        return (settings?.customer_cancellation_enabled ?? true) && withinCancellationWindow(b);
    }
    function handleCancelClick(b) {
        if (canSelfCancel(b)) {
            setCancelModal({ booking: b });
        }
        else {
            setContactModal(true);
        }
    }
    async function handleCancel() {
        if (!cancelModal)
            return;
        setCancelling(true);
        await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', cancelModal.booking.id);
        setBookings((prev) => prev.map((b) => b.id === cancelModal.booking.id ? { ...b, status: 'cancelled' } : b));
        setCancelModal(null);
        setCancelling(false);
    }
    const allItems = [
        ...bookings.map((b) => ({ kind: 'booking', data: b })),
        ...(assessmentAppt ? [{ kind: 'assessment', appt: assessmentAppt }] : []),
    ];
    const getItemDate = (item) => item.kind === 'booking' ? item.data.booking_date : item.appt.preferred_assessment_date;
    const filtered = allItems
        .filter((item) => {
        const upcoming = isFuture(startOfDay(parseISO(getItemDate(item))));
        if (item.kind === 'booking') {
            if (filter === 'upcoming')
                return upcoming && item.data.status !== 'cancelled';
            if (filter === 'past')
                return !upcoming || item.data.status === 'cancelled';
            return true;
        }
        else {
            if (filter === 'upcoming')
                return upcoming;
            if (filter === 'past')
                return !upcoming;
            return true;
        }
    })
        .sort((a, b) => getItemDate(b).localeCompare(getItemDate(a)));
    if (authLoading)
        return null;
    if (!user) {
        navigate('/login', { replace: true });
        return null;
    }
    return (_jsxs(CustomerLayout, { children: [_jsxs("div", { className: "px-5 py-8 md:px-8 md:py-10 max-w-4xl mx-auto", children: [_jsx("h1", { className: "text-2xl font-bold text-brand-ink mb-6", children: "My Bookings" }), _jsx("div", { className: "flex gap-2 mb-6", children: ['upcoming', 'past', 'all'].map((f) => (_jsx("button", { onClick: () => setFilter(f), className: `px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${filter === f
                                ? 'bg-brand-green text-white'
                                : 'bg-white border border-gray-300 text-brand-body hover:bg-gray-50'}`, children: f }, f))) }), loading ? (_jsx("p", { className: "text-gray-400 text-sm", children: "Loading bookings\u2026" })) : filtered.length === 0 ? (_jsxs("div", { className: "text-center py-12", children: [_jsx("p", { className: "text-brand-body mb-4", children: "No bookings found." }), _jsx(Link, { to: "/portal/bookings/new", className: "btn-primary", children: "Book Now" })] })) : (_jsx("ul", { className: "space-y-4", children: filtered.map((item) => {
                            if (item.kind === 'assessment') {
                                const appt = item.appt;
                                return (_jsx("li", { className: "card p-4", children: _jsxs("div", { className: "flex items-start justify-between", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "font-semibold text-brand-ink", children: format(parseISO(appt.preferred_assessment_date), 'EEEE d MMMM yyyy') }), _jsx("p", { className: "text-sm text-brand-body mt-0.5", children: "Assessment Appointment" }), appt.dog_name && (_jsx("p", { className: "text-sm text-brand-body mt-0.5", children: appt.dog_name }))] }), _jsx("div", { className: "text-right shrink-0 ml-3", children: _jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 mt-1", children: "Assessment" }) })] }) }, `assessment-${appt.id}`));
                            }
                            const b = item.data;
                            const dogNames = b.dogs.map((id) => dogMap[id]).filter(Boolean);
                            const extraNames = b.booking_extras.map((be) => be.extra_services?.name).filter(Boolean);
                            return (_jsxs("li", { className: "card p-4", children: [_jsxs("div", { className: "flex items-start justify-between mb-2", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "font-semibold text-brand-ink", children: format(parseISO(b.booking_date), 'EEEE d MMMM yyyy') }), _jsx("p", { className: "text-sm text-brand-body mt-0.5", children: SLOT_LABELS[b.slot_type] }), dogNames.length > 0 && (_jsx("p", { className: "text-sm text-brand-body mt-0.5", children: dogNames.join(', ') })), extraNames.length > 0 && (_jsx("div", { className: "flex flex-wrap gap-1 mt-2", children: extraNames.map((name) => (_jsx("span", { className: "badge-green text-xs", children: name }, name))) }))] }), _jsxs("div", { className: "text-right shrink-0 ml-3", children: [_jsx("p", { className: "text-sm font-semibold text-brand-ink", children: formatGBP(b.total_amount_pence) }), _jsx("span", { className: `${STATUS_STYLES[b.status]} mt-1`, children: STATUS_LABELS[b.status] })] })] }), _jsxs("div", { className: "flex gap-2 mt-3", children: [canEdit(b) && (_jsx(Link, { to: `/portal/bookings/${b.id}/edit`, className: "text-sm px-3 py-1.5 border border-gray-300 rounded-md text-brand-body hover:bg-gray-50 transition-colors", children: "Edit" })), showCancelButton(b) && (_jsx("button", { onClick: () => handleCancelClick(b), className: "text-sm px-3 py-1.5 border border-red-200 rounded-md text-red-600 hover:bg-red-50 transition-colors", children: "Cancel" }))] })] }, b.id));
                        }) }))] }), contactModal && (_jsx("div", { className: "fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "bg-white rounded-xl shadow-xl max-w-sm w-full p-6", children: [_jsx("h2", { className: "text-lg font-bold text-brand-ink mb-2", children: "Cancel booking" }), _jsx("p", { className: "text-sm text-brand-body mb-6", children: "Please contact us to cancel your booking. We'll be happy to help." }), _jsx("button", { onClick: () => setContactModal(false), className: "btn-primary w-full py-2", children: "Close" })] }) })), cancelModal && (_jsx("div", { className: "fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "bg-white rounded-xl shadow-xl max-w-sm w-full p-6", children: [_jsx("h2", { className: "text-lg font-bold text-brand-ink mb-2", children: "Cancel booking?" }), _jsxs("p", { className: "text-sm text-brand-body mb-4", children: [format(parseISO(cancelModal.booking.booking_date), 'EEEE d MMMM yyyy'), " \u2014", ' ', SLOT_LABELS[cancelModal.booking.slot_type], ",", ' ', cancelModal.booking.dog_count, " dog", cancelModal.booking.dog_count !== 1 ? 's' : '', ",", ' ', formatGBP(cancelModal.booking.total_amount_pence)] }), _jsxs("div", { className: "flex gap-3", children: [_jsx("button", { onClick: handleCancel, disabled: cancelling, className: "btn-danger flex-1 py-2", children: cancelling ? 'Cancelling…' : 'Yes, cancel' }), _jsx("button", { onClick: () => setCancelModal(null), className: "btn-ghost flex-1 py-2", children: "Keep booking" })] })] }) }))] }));
}
